import type {
  EditableResumeField,
  OptimizationSuggestion,
} from "@/client/pages/dashboard/ai-optimization/review";

/**
 * Check the model's suggestions against the résumé they claim to rewrite.
 *
 * WHY THIS IS NEEDED: `parseOptimizationSuggestions` validates the SHAPE of each
 * suggestion (non-empty `fieldPath`, `previousText`, `suggestedText`, and that the text
 * actually changed) but nothing verifies it against reality. Two gaps follow, and both are
 * silent:
 *
 *   1. `fieldPath` is not checked against the `editableFields` we authorised. The prompt
 *      says "Suggest changes only for the editable fields listed below" and "Keep each
 *      fieldPath exactly as provided" — but a model that invents or mistypes a path is
 *      taken at its word, and `applyOptimizationSuggestion` uses lodash `_set`, which
 *      CREATES whatever structure the path names. A hallucinated path writes new keys into
 *      the résumé document rather than failing.
 *   2. `previousText` is not compared to the field's current value. The review panel
 *      displays the live value rather than `previousText`, so a suggestion aimed at text
 *      that is not there looks completely normal — and accepting it overwrites the real
 *      content with a rewrite of something else.
 *
 * Neither gap fabricates suggestions: the suggestions ARE the model's. But it means a
 * malformed or misaligned response is applied silently, which is indistinguishable from
 * the AI behaving oddly — exactly the "these don't look AI-generated" symptom.
 *
 * Kept separate from `review.ts` so the existing dashboard optimization flow is untouched.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

export type SuggestionVerification = {
  /** Safe to show and apply. */
  accepted: OptimizationSuggestion[];
  /** Dropped, with the reason — surfaced for logging, not for the student. */
  rejected: Array<{ suggestion: OptimizationSuggestion; reason: string }>;
};

/** Compare rich text by its words, so markup differences are not treated as a mismatch. */
export const normalizeForComparison = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * @param suggestions - output of parseOptimizationSuggestions
 * @param editableFields - the fields we authorised the model to touch
 */
export const verifySuggestions = (
  suggestions: OptimizationSuggestion[],
  editableFields: EditableResumeField[],
): SuggestionVerification => {
  const accepted: OptimizationSuggestion[] = [];
  const rejected: Array<{ suggestion: OptimizationSuggestion; reason: string }> = [];

  // Indexed by the exact fieldPath we sent, which is also the key the model was told to
  // preserve verbatim.
  const authorized = new Map(editableFields.map((field) => [field.fieldPath, field]));

  for (const suggestion of suggestions) {
    const field = authorized.get(suggestion.fieldPath);

    if (!field) {
      rejected.push({
        suggestion,
        reason: `fieldPath "${suggestion.fieldPath}" was not in the editable fields`,
      });
      continue;
    }

    const current = normalizeForComparison(String(field.value ?? ""));
    const claimed = normalizeForComparison(suggestion.previousText);

    // Containment rather than equality: the model is allowed to quote the part of a long
    // field it is rewriting, and rich-text round trips are not byte-stable. What must not
    // pass is text with no relationship to the field at all.
    const aligned = current === claimed || current.includes(claimed) || claimed.includes(current);

    if (!aligned) {
      rejected.push({
        suggestion,
        reason: `previousText does not match the current value of ${suggestion.fieldPath}`,
      });
      continue;
    }

    if (!normalizeForComparison(suggestion.suggestedText)) {
      rejected.push({ suggestion, reason: "suggestedText is empty once markup is stripped" });
      continue;
    }

    accepted.push(suggestion);
  }

  return { accepted, rejected };
};
